/**
 * M07 混淆矩阵热力图 · S2 Day 8b
 *
 * 输入：confusion_matrix list[ConfusionMatrixCell]
 * 渲染：行=expected · 列=actual · 对角线（命中）= success / 非对角（误判）= danger 浓度
 * a11y：纯颜色不够 — cell 内显示数字 · hover 展示完整 expected → actual
 */
import { useMemo } from "react";

import type { ConfusionMatrixCell } from "@/lib/api/eval";

interface Props {
	cells: ConfusionMatrixCell[];
}

function buildIntents(cells: ConfusionMatrixCell[]): string[] {
	const set = new Set<string>();
	for (const c of cells) {
		set.add(c.expected);
		set.add(c.actual);
	}
	return Array.from(set).sort();
}

function buildLookup(cells: ConfusionMatrixCell[]): Map<string, number> {
	const map = new Map<string, number>();
	for (const c of cells) {
		map.set(`${c.expected}|${c.actual}`, c.count);
	}
	return map;
}

function cellTone(expected: string, actual: string, count: number, max: number): string {
	if (count === 0) return "bg-surface-background text-text-muted";
	const intensity = max > 0 ? Math.min(1, count / max) : 0;
	const isHit = expected === actual;
	// 命中走绿色（success），误判走红色（danger）。深浅靠 opacity（避免硬编码颜色）
	const cls = isHit ? "bg-brand-success" : "bg-brand-danger";
	const alpha =
		intensity >= 0.75
			? "opacity-90"
			: intensity >= 0.5
				? "opacity-70"
				: intensity >= 0.25
					? "opacity-50"
					: "opacity-30";
	return `${cls} ${alpha} text-text-inverse`;
}

export function ConfusionMatrixHeatmap({ cells }: Props) {
	const intents = useMemo(() => buildIntents(cells), [cells]);
	const lookup = useMemo(() => buildLookup(cells), [cells]);
	const max = useMemo(() => cells.reduce((m, c) => Math.max(m, c.count), 0), [cells]);

	if (intents.length === 0) {
		return (
			<div
				className="rounded-md border border-dashed border-surface-border bg-surface-card p-6 text-center text-sm text-text-muted"
				data-testid="m07-matrix-empty"
			>
				暂无混淆矩阵数据 · 请先跑回归
			</div>
		);
	}

	return (
		<div
			className="overflow-x-auto rounded-md border border-surface-border bg-surface-card"
			data-testid="m07-confusion-matrix"
		>
			<table className="w-full border-collapse text-xs">
				<caption className="caption-top px-3 py-2 text-left text-sm font-medium text-text-primary">
					混淆矩阵
					<span className="ml-2 text-xs text-text-muted">行 = 期望 · 列 = 实际 · 对角线为命中</span>
				</caption>
				<thead>
					<tr>
						<th
							className="sticky left-0 z-10 bg-surface-subtle px-2 py-2 text-left font-medium text-text-secondary"
							scope="col"
						>
							expected ↓ / actual →
						</th>
						{intents.map((i) => (
							<th
								key={i}
								scope="col"
								className="px-2 py-2 text-left font-mono font-medium text-text-secondary"
							>
								{i}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{intents.map((row) => (
						<tr key={row}>
							<th
								scope="row"
								className="sticky left-0 z-10 border-t border-surface-border bg-surface-subtle px-2 py-2 text-left font-mono font-medium text-text-secondary"
							>
								{row}
							</th>
							{intents.map((col) => {
								const count = lookup.get(`${row}|${col}`) ?? 0;
								const isHit = row === col;
								return (
									<td
										key={col}
										data-testid={`m07-cell-${row}-${col}`}
										className={`border-t border-surface-border px-2 py-2 text-center font-mono ${cellTone(row, col, count, max)}`}
										title={`${row} → ${col}：${count} 条${isHit ? "（命中）" : count > 0 ? "（误判）" : ""}`}
										aria-label={`${row} 实际为 ${col}，共 ${count} 条`}
									>
										{count || ""}
									</td>
								);
							})}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
